<template>
  <div
    v-if="entityCustomFields?.length && organizationCustomFields?.length > 0"
    :data-entity-type="entityType"
    :class="[{ 'custom-fields-in-modal': inModal }, optionalClass]">
    <div
      class="form-section group collapsable"
      :class="{ 'mb-5': !isExpanded }">
      <span class="icon"><icon-colored-custom-field /></span>
      <span v-if="title == null">{{ capitalize($t('names.organizationCustomField', { n: 2 })) }}</span>
      <span v-else>{{ title }}</span>
      <div
        v-if="!inModal"
        class="actions">
        <button
          v-if="showClearButton"
          class="btn btn-danger btn-sm"
          @click="clear">
          <icon-delete />
          {{ capitalize($t('actions.clear')) }}
        </button>
        <form-section-expand-collapse
          v-if="!canBeRemoved"
          :expanded="isExpanded"
          @expanded="isExpanded = true"
          @collapsed="isExpanded = false" />
      </div>
      <button
        v-if="inModal"
        type="button"
        class="close"
        data-dismiss="modal"
        aria-label="Close"
        @click="$emit('closeModal')">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <transition name="fade">
      <div
        v-if="isExpanded"
        class="form-section-content"
        :class="[isExpanded ? 'form-section-expanded' : 'form-section-collapsed', inModal && 'pt-0']">
        <div class="form-group row">
          <div
            id="edit_organization_custom_fields"
            class="col-sm-12">
            <div
              v-for="field in entityCustomFields"
              :key="field.key"
              class="form-group row">
              <form-label
                :for-i-d="`edit_custom-field_${field.key}_name`"
                :is-required="isFieldRequired(field) && entityType !== 'Plan'">
                <span :class="{ 'label-container': entityType === 'Plan' }">
                  {{ getOrganizationCustomFieldProperty(field, 'name') ?? field.key }}
                  <span
                    v-if="isFieldRequired(field) && entityType === 'Plan'"
                    v-tooltip.top="capitalize($t('messages.requiredToSaveAsPlan'))"
                    class="icon-warning-container">
                    <icon-warning-hexagon />
                  </span>
                </span>
              </form-label>

              <organization-custom-field-values-selector
                v-if="canUseList(field)"
                :options="getOrganizationCustomFieldValuesAsListOption(field)"
                :pre-selected-option="getOrganizationCustomFieldValueSelected(field)"
                :organization-custom-field="getOrganizationCustomField(field)"
                :required="getOrganizationCustomFieldRequired(field)"
                :class-names="inModal ? 'pr-0' : ''"
                @select="(selectedOption) => onOptionSelected(field, selectedOption)" />

              <div
                v-else
                class="col-sm-8"
                :class="{ 'pr-0': inModal }">
                <input
                  :id="`edit_custom-field_${field.key}_name`"
                  v-model.trim="field.value"
                  class="edit-custom-field-input"
                  :name="field.key"
                  type="text"
                  :class="{
                    'form-control': true,
                    input: true,
                    'is-danger': validationErrors != null && $validator.messageBag.errors.has(field.name),
                  }"
                  @change="fireChangedEvent" />
                <span
                  v-show="validationErrors != null && $validator.messageBag.errors.has(field.name)"
                  class="help is-danger">
                  {{ validationErrors != null && $validator.messageBag.errors.first(field.name) }}
                </span>
              </div>
              <div
                v-if="getOrganizationCustomFieldDropdown(field) && !canUseList(field)"
                class="col-sm-1 flex">
                <div class="icon-warn">
                  <span v-tooltip.top="capitalize($t('messages.organizationCustomValueNotAvailableInList'))">
                    <icon-warning-filled />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
  import { capitalize, computed, inject, ref } from 'vue';

  import { FormLabel, FormSectionExpandCollapse } from '@/common/components/controls';
  import IconColoredCustomField from '@/common/icons/colored/CustomField.vue';
  import { IconWarningFilled, IconWarningHexagon } from '@/common/icons/monochrome';
  import IconDelete from '@/common/icons/monochrome/Delete.vue';
  import { CampaignProperty, LineItemProperty, OrganizationCustomField, PlanProperty } from '@/common/models';
  import OrganizationCustomFieldValue from '@/common/models/organization-custom-fields/OrganizationCustomFieldValue';
  import MessageCollection from '@/validator/MessageCollection';
  import Validator from '@/validator/Validator';

  import OrganizationCustomFieldValuesSelector from './OrganizationCustomFieldValuesSelector.vue';

  type EntityPropertyType = CampaignProperty | LineItemProperty | PlanProperty;

  interface IProps {
    entityType: string;
    title: string;
    organizationCustomFields: OrganizationCustomField[];
    entityCustomFields: EntityPropertyType[];
    canBeRemoved?: boolean;
    validationErrors: InstanceType<typeof MessageCollection>;
    inModal?: boolean;
    optionalClass?: { [key: string]: boolean };
  }

  const {
    entityType,
    title,
    organizationCustomFields,
    entityCustomFields,
    canBeRemoved,
    validationErrors,
    inModal,
    optionalClass,
  } = defineProps<IProps>();

  const $validator = inject<Validator>('validator');

  const emit = defineEmits(['changed', 'closeModal', 'clear']);

  const isExpanded = ref(true);

  const showClearButton = computed(() => !!entityCustomFields.some((x) => x.value));

  function getOrganizationCustomField(field: EntityPropertyType): OrganizationCustomField {
    return organizationCustomFields.find((x) => x.key === field.key);
  }
  function getOrganizationCustomFieldProperty(field: EntityPropertyType, prop: string): unknown {
    return getOrganizationCustomField(field)?.[prop];
  }

  function getOrganizationCustomFieldRequired(field: EntityPropertyType): boolean {
    return getOrganizationCustomFieldProperty(field, 'isRequired') as boolean;
  }

  function getOrganizationCustomFieldDropdown(field: EntityPropertyType): boolean {
    return getOrganizationCustomFieldProperty(field, 'isDropdown') as boolean;
  }

  function getOrganizationCustomFieldValuesAsListOption(field: EntityPropertyType): OrganizationCustomFieldValue[] {
    return (getOrganizationCustomFieldProperty(field, 'organizationCustomValues') ??
      []) as OrganizationCustomFieldValue[];
  }

  function getOrganizationCustomFieldValueSelected(field: EntityPropertyType): OrganizationCustomFieldValue {
    return getOrganizationCustomFieldValuesAsListOption(field).find((x) => x.value === field.value);
  }

  function canUseList(field: EntityPropertyType): boolean {
    if (field.value && !getOrganizationCustomFieldValueSelected(field)) {
      return false;
    }

    return getOrganizationCustomFieldDropdown(field);
  }

  function fireChangedEvent() {
    emit('changed', entityCustomFields);
  }

  function isFieldRequired(field: EntityPropertyType) {
    return !!entityCustomFields.find((x) => x.key === field.key)?.isRequired;
  }

  function onOptionSelected(field: EntityPropertyType, selectedOption: OrganizationCustomFieldValue) {
    const newEntityCustomFields = [...entityCustomFields].map((x) => {
      if (x.key === field.key) {
        x.value = selectedOption.value;
      }

      return x;
    });
    emit('changed', newEntityCustomFields);
  }

  function clear() {
    const newEntityCustomFields = [...entityCustomFields].map((x) => ({ ...x, value: '' }));
    emit('clear', newEntityCustomFields);
  }
</script>

<style lang="scss" scoped>
  .actions {
    align-items: center;
    gap: 1rem;
  }

  .custom-fields-in-modal {
    position: absolute;
    box-shadow: 0 0 1rem rgba(0, 0, 0, 0.5);
    z-index: 2;
    overflow-x: hidden;
    max-height: 32.5rem;
    width: 55rem;
    bottom: 0;
    right: -13.6rem;
    background: white;

    .form-section {
      margin: 0 !important;
      font-size: 1.4rem !important;

      svg {
        height: 2.2rem;
      }

      .close {
        position: absolute;
        right: 1rem;
        top: 0.2rem;
      }
    }

    .form-section-content {
      padding: 0 !important;
      padding-block: 1rem !important;
      overflow-x: hidden;

      .form-group.row {
        margin-bottom: 0;
      }
    }

    &[data-entity-type='Campaign'] {
      right: unset;
      bottom: unset;
      left: 24%;
      top: -3.5rem;
      width: 68rem;
    }
  }

  .sm-1.flex {
    display: flex;
    align-items: center;
    justify-content: center;

    .icon-warn {
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        stroke: var(--warningDark);
        fill: var(--warningDark);
        height: 1.6rem;
      }
    }
  }

  .label-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    .icon-warning-container svg {
      width: 2rem;
      height: 2rem;
      margin-left: 0.8rem;
      stroke: var(--textColorLight);
    }
  }
</style>
